import React, { useState } from "react";
import s from "./AnalyticsTable.module.scss";
import TextMAS from "components/TextMAS/TextMAS";
import NoResultAnalytics from "./NoResult/NoResultAnalytics";
import { isNullish } from "utils/utils";
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  UnfoldMore,
} from "@mui/icons-material";

const SORT_INIT_STATE = {
  key: "",
  sortingKeyGetter: null,
  asc: undefined,
};

const AnalyticsTable = ({
  columns = [
    {
      id: "",
      title: "",
      key: "",
      headerStyle: {},
      cellStyle: {},
      contentStyle: {},
      sorting: false,
      sortingKey: undefined, //can be string or an accessor lambda function
      defaultValue: "-",
      render: null,
      wrap: false,
    },
  ],
  data = [],
  tableStyle = {},
  initSort = {
    key: "",
    asc: undefined,
  },
  sortingHandler,
}) => {
  const hasData = data instanceof Array && data.length !== 0;
  const getInitSortState = () => {
    if (
      !initSort?.key ||
      !columns.some((column) => column.key === initSort?.key)
    )
      return SORT_INIT_STATE;
    else {
      const targetColumn = columns.find(
        (column) => column.key === initSort?.key,
      );
      console.log(targetColumn);
      return {
        key: initSort?.key,
        sortingKeyGetter:
          typeof targetColumn.sortingKey === "function"
            ? targetColumn.sortingKey
            : ({ data }) => data[targetColumn.sortingKey ?? targetColumn.key],
        asc: initSort.asc ?? undefined,
      };
    }
  };
  const [sort, setSort] = useState(getInitSortState());

  const hasFrontendSort =
    columns.some((column) => column.sorting) &&
    sort.key &&
    sort.sortingKeyGetter &&
    !isNullish(sort.asc);
  const parsedData =
    hasData && hasFrontendSort
      ? [...data].sort((a, b) => {
          const [A, B] = [
            sort.sortingKeyGetter({ data: a, key: sort.key }),
            sort.sortingKeyGetter({ data: b, key: sort.key }),
          ];
          const order = sort.asc ? 1 : -1;
          if (typeof A === "number") {
            return (A - B) * order;
          } else if (typeof A === "string") {
            return A.localeCompare(B) * order;
          } else {
            return 0;
          }
        })
      : data;

  const getSortingIcon = (asc) => {
    return (
      <div className={s["sort-icon"]}>
        {isNullish(asc) ? (
          <UnfoldMore />
        ) : asc ? (
          <KeyboardArrowDown />
        ) : (
          <KeyboardArrowUp />
        )}
      </div>
    );
  };

  //SortOrder asc cycles: undefined > (false > true or true > false) > undef...
  const getNextSortOrder = (sorting, resetAsc, prevAsc) => {
    const isAscFirst = String(sorting).toLowerCase() === "asc";
    return resetAsc || isNullish(prevAsc)
      ? isAscFirst
      : prevAsc === isAscFirst
      ? !isAscFirst
      : undefined;
  };

  //reset sort state if the sorted column does not exist
  if (
    !columns.some(
      (column) =>
        sort.key ===
        (typeof column.sortingKey === "function" || isNullish(column.sortingKey)
          ? column.key
          : column.sortingKey),
    ) &&
    sort.key !== ""
  ) {
    setSort(SORT_INIT_STATE);
  }

  return (
    <div style={{ width: "100%", overflow: "auto" }}>
      <table className={s["analytics-table"]} style={tableStyle}>
        <thead>
          <tr>
            {columns.map((column) => {
              let sortOnClickHandler;
              const classNames = [];

              if (column.sorting) {
                const columnSortingKey =
                  typeof column.sortingKey === "function" ||
                  isNullish(column.sortingKey)
                    ? column.key
                    : column.sortingKey;
                const sortingKeyGetter =
                  typeof column.sortingKey === "function"
                    ? column.sortingKey
                    : ({ data }) => data[columnSortingKey];

                const sortStateHandler = () => {
                  const nextAsc = getNextSortOrder(
                    column.sorting,
                    sort.key !== columnSortingKey,
                    sort.asc,
                  );

                  setSort({
                    key: columnSortingKey,
                    sortingKeyGetter: sortingKeyGetter,
                    asc: nextAsc,
                  });

                  sortingHandler &&
                    sortingHandler({ key: columnSortingKey, asc: nextAsc });
                };

                sortOnClickHandler = sortStateHandler;
                if (sort.key === columnSortingKey && !isNullish(sort.asc))
                  classNames.push(s["sortable-selected"]);
                classNames.push(s["sortable"]);
              }

              return (
                <th
                  key={column.id}
                  onClick={sortOnClickHandler}
                  className={classNames.join(" ")}
                  style={column.headerStyle}
                >
                  <div>
                    {typeof column.title !== "object" ? (
                      <TextMAS fontWeight="semibold">{column.title}</TextMAS>
                    ) : (
                      column.title
                    )}
                    {column.sorting && getSortingIcon(sort.asc)}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {hasData ? (
            parsedData.map((item, index) => {
              return (
                <tr key={index}>
                  {columns.map((column) => {
                    //use default rendering function if not provided
                    const render =
                      column.render ??
                      (({ data, key }) =>
                        data[key] ? data[key] : column.defaultValue);
                    const value = render({
                      data: item,
                      key: column.key,
                      index: index,
                    });

                    return (
                      <td
                        key={column.id}
                        className={column.wrap ? undefined : s["nowrap"]}
                        style={column.cellStyle}
                      >
                        <div style={column.contentStyle}>
                          {typeof value !== "object" ? (
                            <TextMAS>{value}</TextMAS>
                          ) : (
                            value
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })
          ) : (
            <tr>
              <td
                className={s["analytics-table-noData"]}
                colSpan={columns.length}
              >
                <NoResultAnalytics />
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default AnalyticsTable;
